import React, { useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import InfoBanner from "../components/leovera/InfoBanner";
import PromoBanner from "../components/leovera/PromoBanner";
import Header from "../components/leovera/Header";
import Hero from "../components/leovera/Hero";
import BundleSection from "../components/leovera/BundleSection";
import Catalog from "../components/leovera/Catalog";
import BrandStory from "../components/leovera/BrandStory";
import Footer from "../components/leovera/Footer";
import ProductDetailModal from "../components/leovera/ProductDetailModal";
import { api, formatApiError } from "../lib/api";

const Landing = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeProduct, setActiveProduct] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { hash } = useLocation();

  // Scroll to #anchor (e.g. /#kit, /#catalogo) and keep re-aligning while async sections above it load; stops on user input
  useEffect(() => {
    const id = hash ? decodeURIComponent(hash.slice(1)) : "";
    if (!id) return undefined;
    let tries = 0;
    const stop = () => clearInterval(timer);
    const timer = setInterval(() => {
      tries += 1;
      const el = document.getElementById(id);
      if (el) {
        const delta = el.getBoundingClientRect().top - 72;
        if (Math.abs(delta) > 4) window.scrollTo({ top: window.scrollY + delta, behavior: "instant" });
      }
      if (tries > 40) stop();
    }, 100);
    const events = ["wheel", "touchstart", "keydown", "mousedown"];
    events.forEach((e) => window.addEventListener(e, stop, { passive: true }));
    return () => {
      stop();
      events.forEach((e) => window.removeEventListener(e, stop));
    };
  }, [hash]);

  useEffect(() => {
    let alive = true;
    let cancelTimer;
    let pollTimer;

    const fetchCatalog = async (attempt = 1, { silent = false } = {}) => {
      try {
        const { data } = await api.get("/catalog", {
          params: { _t: Date.now() }, // cache-bust
        });
        if (!alive) return;
        setProducts(data || []);
        setError("");
      } catch (e) {
        if (!alive || silent) return;
        // Retry up to 3 times with exponential backoff for transient/cache issues
        if (attempt < 3) {
          cancelTimer = setTimeout(() => fetchCatalog(attempt + 1), 600 * attempt);
          return;
        }
        setError(formatApiError(e));
      } finally {
        if (alive && (attempt >= 3 || !cancelTimer)) setLoading(false);
      }
    };
    fetchCatalog();

    // Instant-sync: refetch when the tab regains focus (user just edited in admin
    // in another tab and switched back to the site).
    const onFocus = () => { if (alive) fetchCatalog(1, { silent: true }); };
    const onVisibility = () => { if (document.visibilityState === "visible") onFocus(); };
    // Cross-tab signal: admin writes to this key after each save.
    const onStorage = (e) => {
      if (e.key === "leovera_catalog_dirty" && alive) fetchCatalog(1, { silent: true });
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("storage", onStorage);

    // Background poll every 30s to guarantee eventual consistency
    // for users who leave the tab open with the same session.
    pollTimer = setInterval(() => {
      if (document.visibilityState === "visible") fetchCatalog(1, { silent: true });
    }, 30000);

    return () => {
      alive = false;
      if (cancelTimer) clearTimeout(cancelTimer);
      if (pollTimer) clearInterval(pollTimer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const retryLoad = useCallback(() => {
    setLoading(true);
    setError("");
    // hard reload to bypass any cached chunks
    window.location.reload();
  }, []);

  const openProduct = useCallback((product) => {
    setActiveProduct(product);
    setModalOpen(true);
  }, []);

  const handleOpenChange = useCallback((open) => {
    setModalOpen(open);
    if (!open) {
      setTimeout(() => setActiveProduct(null), 200);
    }
  }, []);

  return (
    <div
      className="min-h-screen text-white"
      style={{
        backgroundColor: "var(--lv-bg,#060A14)",
        backgroundImage: "var(--lv-bg-image, none)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        backgroundRepeat: "no-repeat",
      }}
      data-testid="leovera-landing-v103"
    >
      <PromoBanner />
      <InfoBanner />
      <Header />
      <main>
        <Hero />
        {!loading && !error && <BundleSection products={products} />}
        {loading ? (
          <div className="py-32 text-center text-[#94A3B8] text-sm tracking-[0.32em] uppercase" data-testid="catalog-loading">
            Caricamento catalogo…
          </div>
        ) : error ? (
          <div className="py-32 text-center" data-testid="catalog-error">
            <p className="text-red-300 text-sm mb-4">{error}</p>
            <button
              onClick={retryLoad}
              data-testid="catalog-retry-button"
              className="px-6 py-2 border border-white/20 text-white text-xs tracking-[0.32em] uppercase hover:bg-white/10 transition"
            >
              Riprova
            </button>
          </div>
        ) : (
          <Catalog products={products} onOpenProduct={openProduct} />
        )}
        <BrandStory />
      </main>
      <Footer />
      <ProductDetailModal
        product={activeProduct}
        open={modalOpen}
        onOpenChange={handleOpenChange}
      />
    </div>
  );
};

export default Landing;
